// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import { SelectPresentationProps } from "@eisinc/MobiusOverrides/Select";
import { TextFieldProps } from "@eisinc/MobiusOverrides/TextField";
import ProductAddToCartButton from "@eisinc/Overrides/Components/ProductAddToCartButton";
import ProductAddToListLink, { ProductAddToListLinkStyles } from "@eisinc/Overrides/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@eisinc/Overrides/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@eisinc/Overrides/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@eisinc/Overrides/Components/ProductDescription";
import { ProductPartNumbersStyles } from "@eisinc/Overrides/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@eisinc/Overrides/Components/ProductPrice";
import ProductQuantityOrdered from "@eisinc/Overrides/Components/ProductQuantityOrdered";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import updateQuantity from "@insite/client-framework/Store/Pages/StaticList/Handlers/UpdateQuantity";
import updateUnitOfMeasure from "@insite/client-framework/Store/Pages/StaticList/Handlers/UpdateUnitOfMeasure";
import translate from "@insite/client-framework/Translate";
import { ProductModel, WishListLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cloneDeep from "lodash/cloneDeep";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    wishListLine: WishListLineModel;
    productInfo: ProductInfo;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
});

const mapDispatchToProps = {
    updateUnitOfMeasure,
    updateQuantity,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface StaticListsProductListLineStyles {
    lineInnerContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    imageWrapper?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    descriptionGridItem?: GridItemProps;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    notesStyles?: SmallHeadingAndTextStyles;
    partNumbersHidden?: HiddenProps;
    partNumbersGridItem?: GridItemProps;
    priceAndAvailabilityGridItem?: GridItemProps;
    priceAndAvailabilityInnerContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    productPriceStyles?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    uomAndQuantityGridItem?: GridItemProps;
    uomAndQuantityInnerContainer?: GridContainerProps;
    uomGridItem?: GridItemProps;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    quantityGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    availabilityGridItem?: GridItemProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    notesHidden?: HiddenProps;
    notesGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    addToCartButton?: ButtonPresentationProps;
    addToListLink?: ProductAddToListLinkStyles;
    // XNG-Change
    infoWrapper?: InjectableCss;
    container?: InjectableCss;
    atcWrapper?: InjectableCss;
    innerInfo?: InjectableCss;
}

export const staticListProductListLineStyles: StaticListsProductListLineStyles = {
    lineInnerContainer: {
        gap: 10,
        css: css`
            border-bottom: 1px lightgray solid;
            padding: 20px 0;
        `,
    },
    imageGridItem: {
        width: [3, 3, 2, 2, 1],
        printWidth: 1,
    },
    imageWrapper: {
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
            }
            width: 100%;
            min-width: 0;
            flex-direction: column;
        `,
    },
    descriptionGridItem: {
        width: [8, 8, 4, 4, 4],
        printWidth: 6,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersStyles: {
        erpNumberLabelText: {
            size: 14,
            weight: "bold",
            lineHeight: "18px",
        },
        customerNameLabelText: {
            size: 14,
            weight: "bold",
            lineHeight: "18px",
        },
        specNumberLabelText: {
            size: 14,
            weight: "bold",
            lineHeight: "18px",
        },
        manufacturerItemLabelText: {
            size: 14,
            weight: "bold",
            lineHeight: "18px",
        },
        erpNumberValueText: {
            size: 14,
            weight: "normal",
            lineHeight: "18px",
        },
        customerNameValueText: {
            size: 14,
            weight: "normal",
            lineHeight: "18px",
        },
        specNumberValueText: {
            size: 14,
            weight: "normal",
            lineHeight: "18px",
        },
        manufacturerItemValueText: {
            size: 14,
            weight: "normal",
            lineHeight: "18px",
        },
    },
    partNumbersHidden: {
        css: css`
            @media print {
                display: block;
            }
            width: 100%;
        `,
    },
    partNumbersGridItem: {
        width: 12,
        css: css`
            padding-left: 0px;
            margin-bottom: 15px !important;
        `,
    },
    priceAndAvailabilityGridItem: {
        width: [12, 12, 4, 4, 5],
        printWidth: 5,
    },
    priceAndAvailabilityInnerContainer: { gap: 10 },
    priceGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    productPriceStyles: {
        priceLabelText: {
            variant: "p",
            transform: "uppercase",
            weight: 600,
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: { typographyProps: { size: 12 } },
    },
    uomAndQuantityGridItem: {
        width: [12, 12, 12, 12, 7],
        printWidth: 7,
    },
    uomAndQuantityInnerContainer: {
        gap: 10,
        css: css`
            @media print {
                input,
                select {
                    height: 18px !important;
                    font-size: 11px !important;
                }
            }
            display: flex;
            align-items: center;
        `,
    },
    uomGridItem: { width: 9, printWidth: 6 },
    quantityGridItem: { width: 3, printWidth: 6 },
    productQuantityOrderedStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        cssOverrides: {
            inputSelect: css`
                @media print {
                    width: 100px;
                }
            `,
            formField: css`
                input {
                    text-align: center;
                }
            `,
        },
    },
    productUnitOfMeasureSelectStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        iconProps: {
            css: css`
                @media print {
                    display: none;
                }
            `,
        },
        cssOverrides: {
            formField: css`
                margin-top: 3px;
            `,
        },
    },
    availabilityGridItem: { width: 12 },
    buttonsGridItem: {
        width: [12, 12, 2, 2, 2],
        printWidth: 0,
        css: css`
            flex-direction: column;
            align-items: center;
        `,
    },
    notesHidden: {
        above: "sm",
        css: css`
            @media print {
                display: none;
            }
            width: 100%;
        `,
    },
    notesGridItem: { width: 12 },
    addToCartButton: {
        css: css`
            width: 100%;
            margin-bottom: 10px;
        `,
    },
    productBrandStyles: {
        logoImage: {
            width: "auto",
            height: "40px",
            css: css`
                max-width: 100%;

                img {
                    max-width: 100%;
                    max-height: 100%;
                    width: auto;
                    height: auto;
                }
            `,
        },
        nameLink: {
            color: "text.main",
        },
        nameText: {
            color: "text.main",
        },
    },
};

const styles = staticListProductListLineStyles;

const StaticListProductListLine = ({
    wishListLine,
    productInfo,
    settingsCollection,
    updateUnitOfMeasure,
    updateQuantity,
}: Props) => {
    const { productSettings } = settingsCollection;
    const product = mapWishListLineToProduct(wishListLine);
    const productContext: ProductContextModel = {
        product,
        productInfo,
        onQtyOrderedChanged: qtyOrdered => {
            updateQuantity({ wishListLineId: wishListLine.id, qtyOrdered });
        },
        onUnitOfMeasureChanged: unitOfMeasure => {
            updateUnitOfMeasure({ wishListLineId: wishListLine.id, unitOfMeasure });
        },
    };

    // XNG-Change: use styledWrapper
    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper
                {...styles.container}
                data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
            >
                <StyledWrapper {...styles.imageWrapper}>
                    <ProductImage
                        product={productContext}
                        extendedStyles={styles.productImageStyles}
                        showSmallImage={false}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.infoWrapper}>
                    <StyledWrapper {...styles.descriptionGridItem}>
                        <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.innerInfo}>
                        <StyledWrapper {...styles.partNumbersGridItem}>
                            <ProductPartNumbers
                                productNumber={product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                extendedStyles={styles.productPartNumbersStyles}
                                showLabel={true}
                                {...({
                                    modelNumber: product.detail?.modelNumber || "",
                                    specNumber: product.properties.specNumber || "",
                                } as any)}
                            />
                        </StyledWrapper>
                        {product.brand && (
                            <StyledWrapper>
                                <ProductBrand
                                    brand={product.brand}
                                    extendedStyles={styles.productBrandStyles}
                                    showLogo={false}
                                    showLabel={true}
                                />
                            </StyledWrapper>
                        )}
                        {wishListLine.notes && (
                            <StyledWrapper {...styles.notesGridItem} className="wishlist-line-notes-grid-item">
                                <SmallHeadingAndText
                                    heading={translate("Notes")}
                                    text={wishListLine.notes}
                                    extendedStyles={styles.notesStyles}
                                />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>

                    <StyledWrapper {...styles.availabilityGridItem}>
                        {productInfo.inventory && (
                            <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                        )}
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...styles.buttonsGridItem}>
                    <StyledWrapper {...styles.priceGridItem}>
                        <ProductPrice
                            product={productContext}
                            showSavings={true}
                            showLabel={false}
                            showSavingsAmount={productSettings.showSavingsAmount}
                            showSavingsPercent={productSettings.showSavingsPercent}
                            extendedStyles={styles.productPriceStyles}
                        />
                        <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.atcWrapper}>
                        <StyledWrapper {...styles.quantityGridItem}>
                            <ProductQuantityOrdered extendedStyles={styles.productQuantityOrderedStyles} />
                        </StyledWrapper>
                        {product.unitOfMeasures && product.unitOfMeasures.length > 1 && (
                            <StyledWrapper {...styles.uomGridItem}>
                                <ProductUnitOfMeasureSelect extendedStyles={styles.productUnitOfMeasureSelectStyles} />
                            </StyledWrapper>
                        )}
                        <ProductAddToCartButton
                            extendedStyles={styles.addToCartButton}
                            data-test-selector="addToCart"
                        />
                    </StyledWrapper>
                    <ProductAddToListLink
                        data-test-selector="addToList"
                        extendedStyles={styles.addToListLink}
                        showIcon={true}
                    />
                </StyledWrapper>
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

const mapWishListLineToProduct = (wishListLine: WishListLineModel) => {
    const product = cloneDeep(wishListLine) as any as ProductModel;
    product.id = wishListLine.productId;
    product.productNumber = wishListLine.erpNumber;
    product.productTitle = wishListLine.shortDescription;
    product.customerProductNumber = wishListLine.customerName;
    product.unitOfMeasures =
        wishListLine?.productUnitOfMeasures?.map(u => ({
            id: u.productUnitOfMeasureId,
            ...u,
        })) || null;
    product.canAddToWishlist = true;
    return product;
};

export default connect(mapStateToProps, mapDispatchToProps)(StaticListProductListLine);
